import {
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type IChartLegendItemProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";
import ChartKey from "../key";

const ChartLegendItem: FC<IChartLegendItemProps> = ({
    shape = "square",
    isToggleable,
    customTheme,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    isLocked,
    onToggle,
    onFocus,
    pattern,
    onBlur,
    isOn,
    item,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled: false,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle,
        plain: plainDynamicStyle
    } = useStyles({
        controlHeights,
        isFocusVisible,
        isToggleable,
        shapeConfigs,
        touchTargets,
        pointerType,
        radiuses,
        borders,
        colors,
        spaces
    });

    const isActiveLayer = !isLocked && (isHovered || isPressed);

    const labelColor = !isOn ? "low" : isActiveLayer ? "high" : "mid";

    const renderContent = () => {
        return <>
            <ChartKey
                colorIndex={item.colorIndex}
                shape={item.shape ?? shape}
                customTheme={customTheme}
                pattern={pattern}
                isOff={!isOn}
            />
            <Text
                style={[
                    stylesheet.label,
                    isOn ? undefined : stylesheet.offLabel
                ]}
                customTheme={customTheme}
                variant="bodySmallSize"
                color={labelColor}
                numberOfLines={1}
            >
                {item.title}
            </Text>
        </>;
    };

    if(!isToggleable) {
        return <View
            style={[
                stylesheet.plain,
                plainDynamicStyle
            ]}
        >
            {renderContent()}
        </View>;
    }

    const toggleProps = Platform.OS === "web" ? {
        "aria-pressed": isOn
    } : {
        "aria-selected": isOn
    };

    return <Pressable
        {...props}
        {...toggleProps}
        {...interactionHandlers}
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
        onPress={() => {
            if(isLocked) {
                return;
            }

            onToggle?.(item.key);
        }}
        aria-disabled={isLocked}
        disabled={isLocked}
        role="button"
    >
        <View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                isHovered={isActiveLayer && isHovered}
                isPressed={isActiveLayer && isPressed}
                borderRadius={radiuses.actions}
                customTheme={customTheme}
            />
            {renderContent()}
        </View>
    </Pressable>;
};
export default ChartLegendItem;
